// Iter 82.6 / Phase E2.2 — Regularization Requests admin.
// HR + Managers list view with inline approve/reject dialog. The
// detail dialog also serves as the Approval Workspace, so we don't
// need a separate workspace surface — keeps the UI focused.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    CheckCircle, XCircle, Eye, FileText, Clock, Buildings,
    User, ArrowSquareOut, Warning, Calendar,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "../../lib/format";
import { DecisionDialog } from "../../components/regularisation/DecisionDialog";
import { RequestHistory } from "../../components/regularisation/RequestHistory";
import { TYPE_LABEL as RGZ_TYPE_LABEL, openEvidence, isAutoAccepted, requestStatusLabel } from "../../lib/regularisation";
import TeamMonthPanel from "../../components/regularisation/TeamMonthPanel";

const STATUS_CLS = {
    Draft: "bg-slate-100 text-slate-600",
    Pending: "bg-amber-100 text-amber-700",
    Approved: "bg-emerald-100 text-emerald-700",
    Rejected: "bg-rose-100 text-rose-700",
    Withdrawn: "bg-slate-100 text-slate-500",
};

const TYPE_LABEL = RGZ_TYPE_LABEL;
const OUTCOME_LABEL = { approved: "Approved", modified: "Changed", rejected: "Rejected", auto_accepted: "Accepted automatically" };

// Iter 102.2 · Attendance Timezone Cleanup — the local `formatDt` helper
// has been removed. Timestamps now use `formatDateTime` from `lib/format`
// which forces Asia/Kolkata timezone (see § J.4 governance rule).
const formatDt = formatDateTime;

export default function RegularizationRequests() {
    const { canAny, can, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [meta, setMeta] = useState({ request_types: [], request_statuses: [] });
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [employees, setEmployees] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    // 102.4 UAT · Defect Fix 001 — Accept prefilled scope + filters via
    // URL query params so the "Start Regularization" button from the
    // snapshot detail dialog lands on a scoped view. Existing behaviour
    // preserved when the URL carries no params.
    const [scope, setScope] = useState(searchParams.get("scope") || "team");
    const [filters, setFilters] = useState({
        status_filter: searchParams.get("status_filter") || "Pending",
        employee_id: searchParams.get("employee_id") || "",
        from_date: searchParams.get("from_date") || "",
        to_date: searchParams.get("to_date") || "",
        department: searchParams.get("department") || "",
    });
    const [page, setPage] = useState(1);
    const [tab, setTab] = useState(searchParams.get("tab") || "requests");

    const [detail, setDetail] = useState(null);
    const [decisionOpen, setDecisionOpen] = useState(null); // 'approve' | 'reject'
    const [decisionRemarks, setDecisionRemarks] = useState("");
    // UAT-07 (2026-07-14) — Inline snapshot modal state. The previous
    // impl navigated to `/admin/attendance/snapshots` which is HR-only;
    // pure managers were bounced through /dashboard → /profile. The
    // inline modal keeps the manager inside the regularization dialog
    // and pulls a scoped payload from
    // `/api/attendance/snapshot/{employee_id}/{attendance_date}` (backend
    // scope guard restricts non-HR callers to actor + direct reports).
    const [snapshotView, setSnapshotView] = useState(null);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError, setSnapshotError] = useState(null);

    const reloadRefs = useCallback(async () => {
        try {
            const [m, hm, e] = await Promise.all([
                api.get("/regularization/meta"),
                api.get("/hr/employee/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }),
            ]);
            setMeta(m.data);
            setHrMeta(hm.data || {});
            setEmployees(e.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load reference data"));
        }
    }, []);

    const reloadRows = useCallback(async () => {
        try {
            const params = { scope, page, page_size: 50 };
            for (const [k, v] of Object.entries(filters)) {
                if (v) params[k] = v;
            }
            const { data } = await api.get("/regularization/request/list", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load requests"));
        }
    }, [scope, filters, page]);

    useEffect(() => { Promise.resolve().then(() => { reloadRefs(); }); }, [reloadRefs]);
    useEffect(() => { Promise.resolve().then(() => { reloadRows(); }); }, [reloadRows]);

    // Deep-link support: ?request_id=RGZ-... auto-opens detail
    useEffect(() => {
        const rid = searchParams.get("request_id");
        if (rid && !detail) {
            api.get(`/regularization/request/${rid}`).then(({ data }) => setDetail(data)).catch(() => {});
        }
    }, [searchParams, detail]);

    const openDetail = async (row) => {
        try {
            const { data } = await api.get(`/regularization/request/${row.id}`);
            setDetail(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load request"));
        }
    };

    const afterDecision = async () => {
        setDecisionOpen(null);
        const { data } = await api.get(`/regularization/request/${detail.id}`);
        setDetail(data);
        reloadRows();
    };

    const goSnapshot = async (row) => {
        // UAT-07 (2026-07-14) — Opens the daily snapshot for the linked
        // (employee_id, attendance_date) inside a nested modal. The
        // backend endpoint enforces the scope guard, so a pure manager
        // can only view snapshots for their direct reports or self —
        // no leak of the org-wide snapshot admin console.
        setSnapshotView({ employeeContext: row, snapshot: null, events: [] });
        setSnapshotLoading(true);
        setSnapshotError(null);
        try {
            const { data } = await api.get(
                `/attendance/snapshot/${encodeURIComponent(row.employee_id)}/${encodeURIComponent(row.attendance_date)}`,
            );
            setSnapshotView({ employeeContext: row, snapshot: data.snapshot, events: data.events || [] });
        } catch (err) {
            setSnapshotError(formatApiError(err, "Failed to load the snapshot for this request."));
        } finally {
            setSnapshotLoading(false);
        }
    };
    const goAnomaly = (row) => {
        if (!row.anomaly_id) return;
        navigate(`/attendance/anomalies?status_filter=all`);
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="regularization-requests-page">
            <PageHeader
                eyebrow="Attendance"
                title="Regularisation Approvals"
                description="Requests are accepted automatically when submitted (if switched on in Month Close settings). The reporting manager can change or reject them until the month is finalised; Super Admin can override at any time. Nobody can decide their own request."
                actions={
                    <div className="inline-flex rounded-md overflow-hidden border border-slate-300" data-testid="rgz-tabs">
                        {[["requests", "Requests"], ["team", "Team month"]].map(([k, l]) => (
                            <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs ${tab === k ? "bg-indigo-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`rgz-tab-${k}`}>{l}</button>
                        ))}
                    </div>
                }
            />
            {tab === "team" ? <div className="px-6 sm:px-8"><TeamMonthPanel /></div> : (<>

            {/* Filters */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end" data-testid="rgz-filters">
                <div>
                    <Label className="text-xs">Scope</Label>
                    <div className="inline-flex rounded-md overflow-hidden border border-slate-300">
                        {["mine", "team", "all"].filter((s) => s !== "all" || user?.role === "super_admin" || can("employee:admin")).map((s) => (
                            <button key={s} onClick={() => { setScope(s); setPage(1); }} className={`px-3 py-1.5 text-xs ${scope === s ? "bg-indigo-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`scope-${s}`}>
                                {s === "mine" ? "Mine" : s === "team" ? "My Team" : "All (HR)"}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <Label className="text-xs">Status</Label>
                    <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.status_filter} onChange={(e) => { setFilters({ ...filters, status_filter: e.target.value }); setPage(1); }} data-testid="filter-status">
                        <option value="Pending">Pending</option>
                        <option value="auto_accepted">Accepted automatically</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Withdrawn">Withdrawn</option>
                        <option value="Draft">Draft</option>
                        <option value="all">All</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Employee</Label>
                    <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.employee_id} onChange={(e) => { setFilters({ ...filters, employee_id: e.target.value }); setPage(1); }} data-testid="filter-employee">
                        <option value="">All</option>
                        {/* 102.4 UAT · Defect Fix 001 — Ensure the URL-supplied
                            employee_id (from the snapshot detail dialog) is
                            always visible as an option, even when it lives
                            outside the /hr/employee/list first page. */}
                        {filters.employee_id && !employees.some((e) => e.id === filters.employee_id) && (
                            <option key="__prefill" value={filters.employee_id}>{filters.employee_id}</option>
                        )}
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Department</Label>
                    <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.department} onChange={(e) => { setFilters({ ...filters, department: e.target.value }); setPage(1); }} data-testid="filter-department">
                        <option value="">All</option>
                        {(hrMeta.departments || []).map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={filters.from_date} onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(1); }} data-testid="filter-from" />
                </div>
                <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={filters.to_date} onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(1); }} data-testid="filter-to" />
                </div>
            </div>

            {/* Table */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5 overflow-x-auto" data-testid="rgz-section">
                <div className="flex justify-between items-center mb-3">
                    <div className="text-xs text-slate-500">{total} request(s)</div>
                    {/* Iter 102.2 · IST disclaimer for the dense table below. */}
                    <div className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="ist-disclaimer">All times are displayed in IST.</div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="text-left px-3 py-2 font-medium">Type</th>
                            <th className="text-left px-3 py-2 font-medium">Date</th>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Department</th>
                            <th className="text-left px-3 py-2 font-medium">Reporting Manager</th>
                            <th className="text-left px-3 py-2 font-medium">Reason</th>
                            <th className="text-left px-3 py-2 font-medium">Pending with</th>
                            <th className="text-left px-3 py-2 font-medium">Attendance Status</th>
                            <th className="text-left px-3 py-2 font-medium">Submitted</th>
                            <th className="w-24" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={11} className="py-10 text-center text-slate-400">No requests for the current filters.</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(r)} data-testid={`request-row-${r.id}`}>
                                <td className="px-3 py-2"><Badge className={`text-[10px] whitespace-nowrap ${STATUS_CLS[r.status]}`} data-testid={`request-status-${r.id}`}>{requestStatusLabel(r)}</Badge></td>
                                <td className="px-3 py-2 text-xs text-slate-700">{TYPE_LABEL[r.request_type] || r.request_type}</td>
                                <td className="px-3 py-2 text-xs text-slate-600 font-mono">{r.attendance_date}</td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="text-slate-700">{r.employee?.full_name || r.employee_id}</div>
                                    <div className="text-[10px] text-slate-400 font-mono">{r.employee?.employee_code}</div>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{r.employee?.department || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">
                                    {r.reporting_manager ? (
                                        <>
                                            <div>{r.reporting_manager.full_name}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{r.reporting_manager.employee_code}</div>
                                        </>
                                    ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{r.reason_label || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{r.pending_with || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{r.attendance_status || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-500">{r.submitted_at ? formatDt(r.submitted_at) : "—"}</td>
                                <td className="px-3 py-2 text-right">
                                    <Eye size={14} className="inline text-slate-400" />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {total > 50 && (
                    <div className="mt-3 flex justify-end gap-2 text-xs items-center">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                        <span>Page {page} of {Math.ceil(total / 50)}</span>
                        <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    </div>
                )}
            </section>

            </>)}
            {/* Detail dialog (also serves as Approval Workspace) */}
            <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDecisionOpen(null); setDecisionRemarks(""); setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("request_id"); return n; }); } }}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="rgz-detail-dialog">
                    {detail && <DetailView
                        request={detail}
                        canDecide={canAny(["employee:read", "manager:read"]) && detail.submitted_by_id !== user?.id}
                        isSuperAdmin={user?.role === "super_admin" && detail.submitted_by_id !== user?.id}
                        onDecision={(action) => { setDecisionOpen(action); setDecisionRemarks(""); }}
                        onConfirmOk={async () => {
                            try { await api.post(`/regularization/request/${detail.id}/confirm`); toast.success("Marked as fine."); await afterDecision(); }
                            catch (err) { toast.error(formatApiError(err, "Could not mark as fine")); }
                        }}
                        onGoSnapshot={() => goSnapshot(detail)}
                        onGoAnomaly={() => goAnomaly(detail)}
                    />}
                </DialogContent>
            </Dialog>

            <DecisionDialog mode={decisionOpen} request={detail} onClose={() => setDecisionOpen(null)} onDone={afterDecision} />

            {/* UAT-07 (2026-07-14) · Inline Snapshot Modal — scoped
                to (employee_id, attendance_date) of the parent regularization
                request. Backend endpoint `/attendance/snapshot/{emp}/{date}`
                enforces the manager-scope guard so pure managers see only
                their own / direct-reports snapshots. */}
            <Dialog open={!!snapshotView} onOpenChange={(o) => { if (!o) { setSnapshotView(null); setSnapshotError(null); } }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rgz-snapshot-dialog">
                    <DialogHeader>
                        <DialogTitle>Attendance Snapshot · {snapshotView?.employeeContext?.employee?.full_name || snapshotView?.employeeContext?.employee_id} · {snapshotView?.employeeContext?.attendance_date}</DialogTitle>
                        <DialogDescription>
                            Read-only view of the daily snapshot and source events for the employee-date pair on this regularization request.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3" data-testid="rgz-snapshot-body">
                        {snapshotLoading && <div className="text-sm text-slate-400 py-8 text-center">Loading snapshot…</div>}
                        {snapshotError && (
                            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="rgz-snapshot-error">
                                {snapshotError}
                            </div>
                        )}
                        {!snapshotLoading && !snapshotError && snapshotView?.snapshot && (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-slate-50 border border-slate-200 rounded-md p-3">
                                    <Field label="Status" value={snapshotView.snapshot.attendance_status || "—"} />
                                    <Field label="Net Working" value={snapshotView.snapshot.net_working_minutes != null ? `${(snapshotView.snapshot.net_working_minutes / 60).toFixed(2)} h` : "—"} />
                                    <Field label="First In" value={snapshotView.snapshot.first_check_in ? formatDt(snapshotView.snapshot.first_check_in) : "—"} />
                                    <Field label="Last Out" value={snapshotView.snapshot.last_check_out ? formatDt(snapshotView.snapshot.last_check_out) : "—"} />
                                    <Field label="Late?" value={snapshotView.snapshot.is_late ? "Yes" : "No"} />
                                    <Field label="Early Depart?" value={snapshotView.snapshot.is_early_departure ? "Yes" : "No"} />
                                    <Field label="Anomaly" value={snapshotView.snapshot.anomaly_code || "—"} />
                                    <Field label="Shift" value={snapshotView.snapshot.shift_code || "—"} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-slate-700 mb-1.5">Source events ({snapshotView.events?.length || 0})</div>
                                    {(!snapshotView.events || snapshotView.events.length === 0) && (
                                        <div className="text-xs text-slate-500 italic">No source events recorded for this date.</div>
                                    )}
                                    {snapshotView.events && snapshotView.events.length > 0 && (
                                        <div className="rounded-md border border-slate-200 overflow-hidden">
                                            <table className="w-full text-xs" data-testid="rgz-snapshot-events-table">
                                                <thead className="bg-slate-50 text-slate-600">
                                                    <tr>
                                                        <th className="text-left px-2 py-1.5 font-medium">Time</th>
                                                        <th className="text-left px-2 py-1.5 font-medium">Type</th>
                                                        <th className="text-left px-2 py-1.5 font-medium">Source</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {snapshotView.events.map((ev, idx) => (
                                                        <tr key={ev.id || idx} className="border-t border-slate-100">
                                                            <td className="px-2 py-1.5 font-mono text-slate-700">{ev.event_time ? formatDt(ev.event_time) : "—"}</td>
                                                            <td className="px-2 py-1.5 text-slate-700">{ev.event_type || "—"}</td>
                                                            <td className="px-2 py-1.5 text-slate-500">{ev.source || "—"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setSnapshotView(null); setSnapshotError(null); }} data-testid="rgz-snapshot-close-btn">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DetailView({ request: r, canDecide, isSuperAdmin, onDecision, onConfirmOk, onGoSnapshot, onGoAnomaly }) {
    const rc = r.requested_correction || {};
    const chain = r.approver_chain || [];
    return (
        <div className="space-y-3" data-testid="rgz-detail-view">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{TYPE_LABEL[r.request_type] || r.request_type}</span>
                        <Badge className={`text-[10px] ${STATUS_CLS[r.status]}`} data-testid="rgz-detail-status">{requestStatusLabel(r)}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{r.id} · {r.attendance_date}</div>
                </div>
                <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={onGoSnapshot} data-testid="goto-snapshot"><Eye size={12} className="mr-1" /> Snapshot</Button>
                    {r.anomaly_id && <Button size="sm" variant="outline" onClick={onGoAnomaly} data-testid="goto-anomaly"><Warning size={12} className="mr-1" /> Anomaly</Button>}
                </div>
            </div>

            {/* Employee context */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <Field label="Employee" value={`${r.employee?.full_name || "—"} (${r.employee?.employee_code || ""})`} />
                <Field label="Department" value={r.employee?.department} />
                <Field label="Reporting Manager" value={r.reporting_manager?.full_name} />
                <Field label="Shift" value={r.shift?.name ? `${r.shift.name} (${r.shift.start_time}–${r.shift.end_time})` : "—"} />
                <Field label="Designation" value={r.employee?.designation} />
                <Field label="Attendance Status" value={r.attendance_status || "—"} />
                <Field label="Submitted by" value={r.submitted_by_email} />
                <Field label="Submitted at" value={formatDt(r.submitted_at)} />
            </div>

            {/* Requested correction */}
            <div className="border border-slate-200 rounded-md p-3 space-y-1">
                <div className="text-xs uppercase tracking-wider text-slate-500">Requested Correction</div>
                <div className="text-sm grid grid-cols-2 gap-2">
                    {rc.check_in_time && <Field label="Check-In" value={formatDt(rc.check_in_time)} />}
                    {rc.check_out_time && <Field label="Check-Out" value={formatDt(rc.check_out_time)} />}
                    {rc.break_end_time && <Field label="Break End" value={formatDt(rc.break_end_time)} />}
                    {rc.break_name && <Field label="Break Name" value={rc.break_name} />}
                </div>
                {rc.notes && <div className="text-xs text-slate-600 mt-1"><b>Notes:</b> {rc.notes}</div>}
                {r.justification && <div className="text-xs text-slate-600 mt-1"><b>Justification:</b> {r.justification}</div>}
            </div>

            <div className="border border-slate-200 rounded-md p-3 text-xs space-y-1" data-testid="rgz-reason-block">
                <div><b>Reason:</b> {r.reason_label || "—"}</div>
                {r.submitted_after_deadline && <Badge className="bg-orange-100 text-orange-800 text-[10px]">Submitted after the {formatDate(r.employee_deadline)} deadline</Badge>}
                {r.evidence_object_id && (
                    <button type="button" className="text-indigo-700 hover:underline" onClick={() => openEvidence(r.evidence_object_id).catch((e) => toast.error(formatApiError(e, "Cannot open evidence")))} data-testid="rgz-evidence-open">
                        Open evidence: {r.evidence_filename || r.evidence_object_id}
                    </button>
                )}
                {r.final_outcome && (
                    <div className="pt-1" data-testid="rgz-final-outcome"><b>Final outcome:</b> {OUTCOME_LABEL[r.final_outcome.decision] || r.final_outcome.decision} → {r.final_outcome.final_status || "—"}{isAutoAccepted(r) ? "" : ` by ${r.final_outcome.decided_by_name || r.final_outcome.decided_by}`}</div>
                )}
            </div>
            <RequestHistory request={r} />

            {/* Approval chain */}
            <div className="border border-slate-200 rounded-md p-3 space-y-2">
                <div className="text-xs uppercase tracking-wider text-slate-500">Approval Chain — {r.approval_path}</div>
                {chain.length === 0 && <div className="text-xs text-slate-400">No approvers configured.</div>}
                {chain.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center ${s.decision === "approved" ? "bg-emerald-100 text-emerald-700" : s.decision === "rejected" ? "bg-rose-100 text-rose-700" : i === chain.findIndex((x) => x.decision === null) ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>
                            {s.decision === "approved" ? <CheckCircle size={12} /> : s.decision === "rejected" ? <XCircle size={12} /> : <Clock size={12} />}
                        </div>
                        <div className="flex-1">
                            <div className="font-medium text-slate-800">{s.role.replace("_", " ").toUpperCase()}</div>
                            <div className="text-[10px] text-slate-400 font-mono">approver: {s.approver_id || "—"}</div>
                            {s.decided_at && <div className="text-[10px] text-slate-500">{formatDt(s.decided_at)} · {s.decided_by_email}</div>}
                            {s.remarks && <div className="text-[10px] text-slate-600 italic mt-0.5">&ldquo;{s.remarks}&rdquo;</div>}
                        </div>
                    </div>
                ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-100">
                {r.status === "Pending" && canDecide && (
                    <>
                        <Button variant="outline" onClick={() => onDecision("reject")} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="reject-btn"><XCircle size={14} className="mr-1" /> Reject</Button>
                        <Button variant="outline" onClick={() => onDecision("modify")} data-testid="modify-btn">Approve with changes</Button>
                        <Button onClick={() => onDecision("approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    </>
                )}
                {isAutoAccepted(r) && canDecide && !r.month_finalised && (
                    <>
                        <Button variant="outline" onClick={() => onDecision("reject")} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="review-reject-btn"><XCircle size={14} className="mr-1" /> Reject</Button>
                        <Button variant="outline" onClick={() => onDecision("modify")} data-testid="review-change-btn">Change</Button>
                        <Button onClick={onConfirmOk} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-ok-btn"><CheckCircle size={14} className="mr-1" /> Looks fine</Button>
                    </>
                )}
                {isAutoAccepted(r) && canDecide && r.month_finalised && !isSuperAdmin && <span className="text-xs text-slate-500" data-testid="rgz-finalised-note">Month finalised — only Super Admin can change this now.</span>}
                {isSuperAdmin && ["Pending", "Approved", "Rejected"].includes(r.status) && (
                    <Button variant="outline" className="border-slate-900 text-slate-900" onClick={() => onDecision("override")} data-testid="override-btn">Super Admin override</Button>
                )}
                {r.status === "Pending" && !canDecide && <span className="text-xs text-slate-500" data-testid="rgz-own-request-note">You cannot decide your own request.</span>}
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-slate-800">{value || "—"}</div>
        </div>
    );
}
